<template>
  <v-container fluid class="invite-friends-view pa-6">
    <div class="invite-friends__header">
      <h1 class="text-h4 font-weight-bold mb-2">
        Invite friends to {{ $config.productName }}
      </h1>
      <p class="text-body-1 mb-0">
        Get <strong>{{ creditsPerSide }} free credits</strong> for each friend
        who joins, and they get
        <strong>{{ creditsPerSide }} credits</strong> too. Up to
        {{ totalCap }} successful referrals.
      </p>
    </div>

    <!-- Progress card -->
    <v-card outlined class="progress-card my-6">
      <v-card-text>
        <div class="d-flex align-center flex-wrap" style="gap: 24px">
          <div class="progress-card__stat">
            <div class="stat-label">Successful referrals</div>
            <div class="stat-value">
              {{ progress.successful_count }} / {{ progress.total_cap }}
            </div>
          </div>
          <div class="progress-card__stat">
            <div class="stat-label">Credits earned</div>
            <div class="stat-value stat-value--accent">
              +{{ progress.credits_earned }}
            </div>
          </div>
          <div class="progress-card__stat flex-grow-1" style="min-width: 240px">
            <div class="stat-label">Progress to cap</div>
            <v-progress-linear
              :model-value="progressPercent"
              :value="progressPercent"
              height="10"
              rounded
              color="orange"
              class="mt-2"
            />
            <div class="text-caption mt-1 text-grey">
              {{ progress.remaining_total_slots }} more referral{{
                progress.remaining_total_slots === 1 ? "" : "s"
              }}
              available.
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Shareable link -->
    <v-card outlined class="my-4">
      <v-card-title class="text-subtitle-1"
        >Your personal invite link</v-card-title
      >
      <v-card-text>
        <p class="text-body-2 mb-3">
          Share this link anywhere - DMs, social, email. Anyone who registers
          using it will count toward your referrals when they verify their
          email.
        </p>
        <div class="share-link-row">
          <v-text-field
            v-model="shareUrl"
            readonly
            hide-details
            outlined
            dense
            class="share-link-row__input"
          />
          <v-btn
            color="primary"
            :disabled="!shareUrl"
            height="40"
            class="share-link-row__button"
            @click="copyShareUrl"
          >
            <v-icon left>mdi-content-copy</v-icon>
            {{ copyButtonLabel }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Send-by-email form -->
    <v-card outlined class="my-4">
      <v-card-title class="text-subtitle-1"
        >Send an invitation by email</v-card-title
      >
      <v-card-text>
        <v-form ref="sendForm" v-model="formValid" @submit.prevent="onSend">
          <v-text-field
            v-model="form.email"
            label="Friend's email"
            type="email"
            outlined
            dense
            :rules="emailRules"
            required
            class="mb-2"
          />
          <v-textarea
            v-model="form.message"
            label="Message (optional, editable)"
            rows="3"
            outlined
            dense
            auto-grow
            counter="1000"
            :rules="messageRules"
            hint="They'll see this in the invitation email."
            persistent-hint
          />
          <div class="d-flex align-center mt-3" style="gap: 12px">
            <v-btn
              color="primary"
              :disabled="!formValid || sending || capReached"
              :loading="sending"
              type="submit"
            >
              Send invitation
            </v-btn>
            <span v-if="capReached" class="text-caption text-grey">
              You've reached the maximum of {{ totalCap }} referrals.
            </span>
            <v-spacer />
            <span class="text-caption text-grey">
              Up to 10 invitations per day.
            </span>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Sent invitations table -->
    <v-card outlined class="my-4">
      <v-card-title class="text-subtitle-1">Sent invitations</v-card-title>
      <v-card-text>
        <v-data-table
          v-if="invitations.length"
          :items="invitations"
          :headers="invitationHeaders"
          dense
          hide-default-footer
          :items-per-page="100"
        >
          <template #[`item.status`]="{ item }">
            <v-chip :color="statusColor(item.status)" small label>
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template #[`item.sent_at`]="{ item }">
            {{ formatDate(item.sent_at) }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="item.status === 'pending'"
              small
              text
              color="error"
              :loading="revoking[item.id]"
              @click="onRevoke(item)"
            >
              Revoke
            </v-btn>
          </template>
        </v-data-table>
        <div v-else class="text-body-2 text-grey">
          No invitations sent yet. Send your first one above.
        </div>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import api from "@shared/services/api";
import config from "@/config";
import { aiProcessing } from "@shared/utils/productWording";

export default {
  name: "InviteFriendsView",
  data() {
    return {
      shareUrl: "",
      progress: {
        successful_count: 0,
        payout_cap: 5,
        total_cap: 5,
        credits_earned: 0,
        credits_per_side: config.REFERRAL_CREDITS_PER_SIDE || 100,
        remaining_payout_slots: 5,
        remaining_total_slots: 5
      },
      invitations: [],
      form: {
        email: "",
        message: this.defaultMessage()
      },
      formValid: false,
      sending: false,
      revoking: {},
      copyButtonLabel: "Copy",
      snackbar: { show: false, text: "", color: "success" },
      emailRules: [
        v => !!v || "Email is required",
        v =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Enter a valid email address"
      ],
      messageRules: [
        v =>
          !v || v.length <= 1000 || "Message must be 1000 characters or fewer"
      ],
      invitationHeaders: [
        { text: "Email", value: "invitee_email", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Sent", value: "sent_at", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" }
      ]
    };
  },
  computed: {
    creditsPerSide() {
      return this.progress.credits_per_side || 100;
    },
    payoutCap() {
      return this.progress.payout_cap || 5;
    },
    totalCap() {
      return this.progress.total_cap || 5;
    },
    progressPercent() {
      var total = this.totalCap || 1;
      var pct = (this.progress.successful_count / total) * 100;
      return Math.max(0, Math.min(100, pct));
    },
    capReached() {
      return this.progress.successful_count >= this.totalCap;
    }
  },
  async mounted() {
    // Apps with no referral programme hide every link here, but the route
    // is still addressable - bounce rather than render a page whose API
    // calls all 404.
    if (config.REFERRALS_ENABLED === false) {
      this.$router.replace("/").catch(() => {});
      return;
    }
    await this.refreshAll();
  },
  methods: {
    defaultMessage() {
      return (
        "Hey, thought you might like " +
        config.PRODUCT_NAME +
        " - " +
        (config.INVITE_TAGLINE || (aiProcessing() ? "it runs AI processing jobs on your files automatically" : "it runs jobs on your files automatically")) +
        ". Sign up with my invite and we both get " +
        // Called from data(), where computed props don't exist yet - read
        // the per-app payout straight from config (absent = shared default).
        (config.REFERRAL_CREDITS_PER_SIDE || 100) +
        " free credits."
      );
    },

    async refreshAll() {
      await Promise.all([
        this.loadShareUrl(),
        this.loadProgress(),
        this.loadInvitations()
      ]);
    },

    async loadShareUrl() {
      try {
        var resp = await api.getMyReferralCode();
        this.shareUrl =
          resp.data && resp.data.share_url ? resp.data.share_url : "";
      } catch (err) {
        // Non-fatal - page works without the share URL
        console.error("Failed to load referral code:", err);
      }
    },

    async loadProgress() {
      try {
        var resp = await api.getReferralProgress();
        if (resp.data) this.progress = resp.data;
      } catch (err) {
        console.error("Failed to load referral progress:", err);
      }
    },

    async loadInvitations() {
      try {
        var resp = await api.listReferralInvitations();
        this.invitations = Array.isArray(resp.data) ? resp.data : [];
      } catch (err) {
        console.error("Failed to load invitations:", err);
        this.invitations = [];
      }
    },

    async copyShareUrl() {
      if (!this.shareUrl) return;
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.copyButtonLabel = "Copied!";
        setTimeout(() => {
          this.copyButtonLabel = "Copy";
        }, 1500);
      } catch (err) {
        this.notify(
          "Couldn't copy. Select the link and copy manually.",
          "error"
        );
      }
    },

    async onSend() {
      if (!this.formValid) return;
      this.sending = true;
      try {
        await api.sendReferralInvitation(
          this.form.email.trim(),
          this.form.message
        );
        this.notify("Invitation sent.", "success");
        this.form.email = "";
        this.form.message = this.defaultMessage();
        if (this.$refs.sendForm && this.$refs.sendForm.resetValidation) {
          this.$refs.sendForm.resetValidation();
        }
        await this.refreshAll();
      } catch (err) {
        var detail =
          (err.response && err.response.data && err.response.data.detail) ||
          "Could not send the invitation.";
        this.notify(detail, "error");
      } finally {
        this.sending = false;
      }
    },

    async onRevoke(item) {
      this.$set
        ? this.$set(this.revoking, item.id, true)
        : (this.revoking[item.id] = true);
      try {
        await api.revokeReferralInvitation(item.id);
        this.notify("Invitation revoked.", "success");
        await this.loadInvitations();
      } catch (err) {
        var detail =
          (err.response && err.response.data && err.response.data.detail) ||
          "Could not revoke the invitation.";
        this.notify(detail, "error");
      } finally {
        this.revoking[item.id] = false;
      }
    },

    statusColor(status) {
      if (status === "registered") return "success";
      if (status === "pending") return "warning";
      if (status === "expired") return "grey";
      if (status === "revoked") return "grey-darken-1";
      return "grey";
    },

    statusLabel(status) {
      if (status === "registered") return "Joined";
      if (status === "pending") return "Pending";
      if (status === "expired") return "Expired";
      if (status === "revoked") return "Revoked";
      return status;
    },

    formatDate(value) {
      if (!value) return "";
      try {
        var d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch (e) {
        return value;
      }
    },

    notify(text, color) {
      this.snackbar = { show: true, text: text, color: color || "info" };
    }
  }
};
</script>

<style scoped>
.invite-friends-view {
  max-width: 980px;
  margin: 0 auto;
}

.invite-friends__header {
  border-left: 4px solid #fb923c;
  padding: 8px 0 8px 16px;
  margin-bottom: 8px;
}

/* Group the share-URL field and Copy button on one row, both pinned to
   the same height so they align cleanly. v-text-field outlined dense
   renders the input box at ~40px tall; matching the button via
   height="40" + align-items: center gives us a true inline pair. */
.share-link-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.share-link-row__input {
  flex: 1 1 auto;
  min-width: 0;
}
.share-link-row__button {
  flex: 0 0 auto;
  /* Nudge to align with v-text-field's visual baseline; the input's
     wrapper carries a few px of top space that the button doesn't. */
  margin-top: 7px;
}
/* v-text-field's hidden bottom slot still reserves space in some
   themes - collapse it so the input's bounding box equals its visible
   border, which then equals the button's height. */
.share-link-row__input ::v-deep(.v-input__slot) {
  margin-bottom: 0;
}
.share-link-row__input ::v-deep(.v-text-field__details) {
  display: none;
}

.progress-card {
  border-left: 4px solid #fb923c;
}

.progress-card__stat {
  min-width: 160px;
}

.stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 22px;
  font-weight: 600;
}

.stat-value--accent {
  color: #c2410c;
}
</style>
